'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Shield, X, ChevronRight, Download, User, Trash2, FileText } from 'lucide-react';
import AppLayout from '@/components/AppLayout';
import { getUnreadAlertCount, getUnreadMessageCount } from '@/lib/demoData';
import { getFamilySettings, updateFamilySettings } from '@/lib/familySettings';

interface ToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}

function Toggle({ checked, onChange, ariaLabel }: ToggleProps) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className="relative inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-lucat-green focus:ring-offset-2 flex-shrink-0"
      style={{ width: 48, height: 28, backgroundColor: checked ? '#4CBF1A' : '#D1D5DB' }}
    >
      <span
        className="inline-block bg-white rounded-full shadow transition-transform"
        style={{ width: 22, height: 22, transform: checked ? 'translateX(22px)' : 'translateX(3px)' }}
      />
    </button>
  );
}

type ModalType = 'account' | 'delete' | 'download' | 'policy' | null;

export default function PrivacyClient() {
  const router = useRouter();
  const unreadAlerts = getUnreadAlertCount();
  const unreadMessages = getUnreadMessageCount();

  const [activityHistory, setActivityHistory] = useState(true);
  const [modal, setModal] = useState<ModalType>(null);
  const [downloadDone, setDownloadDone] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);

  useEffect(() => {
    const s = getFamilySettings();
    setActivityHistory(s.privacy.activityHistoryEnabled);
  }, []);

  const handleActivityToggle = (v: boolean) => {
    setActivityHistory(v);
    updateFamilySettings({ privacy: { activityHistoryEnabled: v } });
  };

  const handleDownload = () => {
    setDownloadDone(true);
    setTimeout(() => { setDownloadDone(false); setModal(null); }, 2500);
  };

  const closeModal = () => {
    setModal(null);
    setDeleteConfirmed(false);
    setDownloadDone(false);
  };

  return (
    <AppLayout unreadAlerts={unreadAlerts} unreadMessages={unreadMessages}>
      {/* Sub-header */}
      <div className="bg-lucat-navy px-4 pt-4 pb-5">
        <button
          onClick={() => router.push('/settings')}
          className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium mb-4 transition-colors"
          style={{ minHeight: 44 }}
          aria-label="Retour aux paramètres"
        >
          <ArrowLeft size={18} />
          Retour
        </button>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Shield size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Confidentialité</h1>
            <p className="text-sm text-white/70 mt-0.5">Gérez les données et la sécurité du compte</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-5 space-y-4 pb-8">
        {/* Section 1 — Données d'André */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h2 className="text-sm font-bold text-lucat-navy mb-2">Données d'André</h2>
          <p className="text-sm text-gray-600 mb-3 leading-relaxed">
            Les informations d'André sont uniquement accessibles aux proches autorisés.
          </p>
          <button
            onClick={() => router.push('/contacts')}
            className="w-full flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
            style={{ minHeight: 48 }}
            aria-label="Voir les personnes autorisées à accéder aux données d'André"
          >
            <span className="text-sm font-semibold text-lucat-navy">Voir les personnes autorisées</span>
            <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
          </button>
        </div>

        {/* Section 2 — Historique */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h2 className="text-sm font-bold text-lucat-navy mb-2">Historique des activités</h2>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">Conserver l'historique des activités</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                Permet de consulter les appels, messages, photos, connexions et usages récents
              </p>
            </div>
            <div className="flex-shrink-0 pt-0.5">
              <Toggle
                checked={activityHistory}
                onChange={handleActivityToggle}
                ariaLabel="Conserver l'historique des activités d'André"
              />
            </div>
          </div>
        </div>

        {/* Section 3 — Données personnelles */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h2 className="text-sm font-bold text-lucat-navy mb-3">Données personnelles</h2>
          <div className="space-y-2">
            <button
              onClick={() => setModal('download')}
              className="w-full flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              style={{ minHeight: 48 }}
              aria-label="Télécharger mes données personnelles"
            >
              <Download size={18} className="text-lucat-navy flex-shrink-0" />
              <span className="text-sm font-semibold text-gray-800">Télécharger mes données</span>
            </button>
            <button
              onClick={() => setModal('account')}
              className="w-full flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              style={{ minHeight: 48 }}
              aria-label="Consulter les informations du compte"
            >
              <User size={18} className="text-lucat-navy flex-shrink-0" />
              <span className="text-sm font-semibold text-gray-800">Consulter les informations du compte</span>
            </button>
            <button
              onClick={() => setModal('delete')}
              className="w-full flex items-center gap-3 bg-red-50 rounded-xl px-4 py-3 hover:bg-red-100 active:bg-red-200 transition-colors"
              style={{ minHeight: 48 }}
              aria-label="Demander la suppression du compte"
            >
              <Trash2 size={18} className="text-red-500 flex-shrink-0" />
              <span className="text-sm font-semibold text-red-600">Demander la suppression du compte</span>
            </button>
          </div>
        </div>

        {/* Section 4 — Mentions */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h2 className="text-sm font-bold text-lucat-navy mb-2">Mentions de confidentialité</h2>
          <button
            onClick={() => setModal('policy')}
            className="w-full flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 hover:bg-gray-100 active:bg-gray-200 transition-colors"
            style={{ minHeight: 48 }}
            aria-label="Consulter la politique de confidentialité"
          >
            <FileText size={18} className="text-lucat-navy flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-800">Consulter la politique de confidentialité</span>
            <ChevronRight size={16} className="text-gray-400 flex-shrink-0 ml-auto" />
          </button>
        </div>
      </div>

      {/* Modals */}
      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md mx-0 sm:mx-4 max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Download modal */}
            {modal === 'download' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-lucat-navy">Télécharger mes données</h3>
                  <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-100" aria-label="Fermer">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>
                {downloadDone ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-lucat-green/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Download size={24} className="text-lucat-green" />
                    </div>
                    <p className="text-sm font-semibold text-gray-800">La préparation de vos données a été lancée.</p>
                    <p className="text-xs text-gray-500 mt-1">Vous recevrez un lien par e-mail sous 24h.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                      Vous pouvez télécharger l'ensemble des données associées à votre compte LUCAT CONNECT FAMILLE.
                    </p>
                    <button
                      onClick={handleDownload}
                      className="w-full bg-lucat-green text-white font-bold text-sm rounded-2xl py-4 hover:bg-lucat-green/90 active:scale-95 transition-all"
                      aria-label="Lancer la préparation de mes données"
                    >
                      Lancer la préparation
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Account info modal */}
            {modal === 'account' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-lucat-navy">Informations du compte</h3>
                  <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-100" aria-label="Fermer">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Nom', value: 'Marie Beaumont' },
                    { label: 'Rôle', value: 'Administratrice familiale' },
                    { label: 'E-mail', value: 'marie@lucat-famille.fr' },
                    { label: 'Proche suivi', value: 'André Beaumont' },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-gray-50 rounded-xl px-4 py-3">
                      <p className="text-xs text-gray-500 font-medium">{label}</p>
                      <p className="text-sm font-semibold text-gray-800 mt-0.5">{value}</p>
                    </div>
                  ))}
                </div>
                <button
                  onClick={closeModal}
                  className="w-full mt-5 bg-lucat-navy text-white font-bold text-sm rounded-2xl py-4 hover:bg-lucat-navy/90 active:scale-95 transition-all"
                  aria-label="Fermer les informations du compte"
                >
                  Fermer
                </button>
              </div>
            )}

            {/* Delete account modal */}
            {modal === 'delete' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-red-600">Demander la suppression</h3>
                  <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-100" aria-label="Fermer">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>
                {deleteConfirmed ? (
                  <div className="text-center py-4">
                    <p className="text-sm font-semibold text-gray-800">Demande enregistrée.</p>
                    <p className="text-xs text-gray-500 mt-1">Cette action n'est pas réalisée dans le prototype.</p>
                    <button onClick={closeModal} className="mt-4 w-full bg-lucat-navy text-white font-bold text-sm rounded-2xl py-4" aria-label="Fermer">Fermer</button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                      La suppression du compte désactiverait l'accès à LUCAT CONNECT FAMILLE. Cette action n'est pas réalisée dans le prototype.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={closeModal}
                        className="flex-1 bg-gray-100 text-gray-700 font-bold text-sm rounded-2xl py-4 hover:bg-gray-200 active:scale-95 transition-all"
                        aria-label="Annuler la suppression"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={() => setDeleteConfirmed(true)}
                        className="flex-1 bg-red-500 text-white font-bold text-sm rounded-2xl py-4 hover:bg-red-600 active:scale-95 transition-all"
                        aria-label="Confirmer la demande de suppression"
                      >
                        Confirmer la demande
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Privacy policy modal */}
            {modal === 'policy' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-lucat-navy">Politique de confidentialité</h3>
                  <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-100" aria-label="Fermer">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>
                <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
                  <p className="text-xs text-orange-600 font-semibold bg-orange-50 rounded-lg px-3 py-2">
                    Contenu provisoire — prototype de démonstration
                  </p>
                  <p><strong className="text-gray-800">Collecte des données</strong><br />LUCAT CONNECT collecte uniquement les données nécessaires au fonctionnement du service de lien familial.</p>
                  <p><strong className="text-gray-800">Utilisation</strong><br />Les données sont utilisées pour faciliter les communications entre André et ses proches autorisés. Elles ne sont jamais vendues à des tiers.</p>
                  <p><strong className="text-gray-800">Conservation</strong><br />Les données sont conservées pendant la durée d'utilisation du service, puis supprimées sur demande.</p>
                  <p><strong className="text-gray-800">Droits</strong><br />Vous pouvez à tout moment accéder à vos données, les corriger ou demander leur suppression via les paramètres de l'application.</p>
                </div>
                <button
                  onClick={closeModal}
                  className="w-full mt-5 bg-lucat-navy text-white font-bold text-sm rounded-2xl py-4 hover:bg-lucat-navy/90 active:scale-95 transition-all"
                  aria-label="Fermer la politique de confidentialité"
                >
                  Fermer
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
